import { useState } from "react";
import dynamic from "next/dynamic";

import type {
  LeaderboardCompany,
  LeaderboardColumn,
} from "lib/types/leaderboards";

import Cell from "./cell/cell";
import classNames from "classnames";

const CompanyModal = dynamic(() => import("./companyModal/companyModal"), {
  ssr: false,
});

type RowProps = {
  company: LeaderboardCompany;
  columns: LeaderboardColumn[];
  onTrackMixpanel: (
    event: string,
    data: {
      [key: string]: string;
    }
  ) => Promise<void>;
  isProUser: boolean;
};

const Row = ({ columns, company, onTrackMixpanel, isProUser }: RowProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModalChange = (open) => {
    setIsModalOpen(open);
  };

  const handleClickOnRow = async () => {
    await onTrackMixpanel("Leaderboard company info click", {
      Component: "Leaderboard Table",
      "Click text": company.companyName,
    });

    handleOpenModalChange(true);
  };

  return (
    <>
      <CompanyModal
        isOpen={isModalOpen}
        onOpenChange={handleOpenModalChange}
        company={company}
        columns={columns}
        isProUser={isProUser}
      />
      <button
        className={classNames(
          "flex w-full bg-[#FFFFFF] p-4 text-left",
          "focus:outline-none focus-visible:outline-none focus-visible:ring focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cerise"
        )}
        onClick={handleClickOnRow}
        aria-expanded={isModalOpen ? "true" : "false"}
        aria-haspopup={"dialog"}
        aria-label={"Open dialog"}
      >
        <div
          className={
            "flex w-full flex-row flex-wrap justify-between gap-[18px] lg:flex-nowrap"
          }
        >
          <div
            className={
              "flex w-full max-w-[100%] grow flex-row flex-nowrap gap-[18px]"
            }
          >
            <div className={"flex flex-col gap-[14px]"}>
              <p
                className={"font-bold text-[14px] leading-[130%] text-mono-20"}
              >
                Rank
              </p>
              <Cell isProUser={isProUser} column={"rank"} company={company} />
            </div>
            <div className={"flex grow flex-col gap-[14px]"}>
              <p
                className={"font-bold text-[14px] leading-[130%] text-mono-20"}
              >
                Company info
              </p>
              <Cell
                isProUser={isProUser}
                column={"companyInfo"}
                company={company}
              />
            </div>
          </div>
          <div
            className={
              "ml-[52px] flex flex-row flex-wrap gap-[30px] text-[16px] leading-[130%] md:flex-[1_0_auto] md:flex-nowrap lg:ml-0 lg:justify-end lg:gap-[18px]"
            }
          >
            {columns.includes("location") && (
              <div
                className={
                  "flex flex-[0_0_calc(50%_-_26px)] flex-col gap-[14px] md:flex-[0_0_100px]"
                }
              >
                <p
                  className={
                    "font-bold text-[14px] leading-[130%] text-mono-20"
                  }
                >
                  HQ
                </p>
                <Cell
                  isProUser={isProUser}
                  column={"location"}
                  company={company}
                />
              </div>
            )}
            {columns.includes("employees") && (
              <div
                className={
                  "flex flex-[0_0_calc(50%_-_26px)] flex-col gap-[14px] md:flex-[0_0_75px]"
                }
              >
                <p
                  className={
                    "font-bold text-[14px] leading-[130%] text-mono-20"
                  }
                >
                  Employees
                </p>
                <Cell
                  isProUser={isProUser}
                  column={"employees"}
                  company={company}
                />
              </div>
            )}
            {columns.includes("totalFunding") && (
              <div
                className={
                  "flex flex-[0_0_calc(50%_-_26px)] flex-col gap-[14px] md:flex-[0_0_90px]"
                }
              >
                <p
                  className={
                    "font-bold text-[14px] leading-[130%] text-mono-20"
                  }
                >
                  Total funding
                </p>
                <Cell
                  isProUser={isProUser}
                  column={"totalFunding"}
                  company={company}
                />
              </div>
            )}
            {columns.includes("latestRevenue") && (
              <div
                className={
                  "flex flex-[0_0_calc(50%_-_26px)] flex-col gap-[14px] md:flex-[0_0_100px]"
                }
              >
                <p
                  className={
                    "font-bold text-[14px] leading-[130%] text-mono-20"
                  }
                >
                  Latest revenue
                </p>
                <Cell
                  isProUser={isProUser}
                  column={"latestRevenue"}
                  company={company}
                />
              </div>
            )}
            {columns.includes("revenueCagr") && (
              <div
                className={
                  "flex flex-[0_0_calc(50%_-_26px)] flex-col gap-[14px] md:flex-[0_0_100px]"
                }
              >
                <p
                  className={
                    "font-bold text-[14px] leading-[130%] text-mono-20"
                  }
                >
                  Revenue CAGR
                </p>
                <Cell
                  isProUser={isProUser}
                  column={"revenueCagr"}
                  company={company}
                />
              </div>
            )}
          </div>
        </div>
      </button>
    </>
  );
};

export default Row;
